import TwoFactorVerify from './TwoFactorVerify';
import OrgSelector from './OrgSelector';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Building2, Home, LogIn } from 'lucide-react';

const LandlordLogin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [show2FA, setShow2FA] = useState(false);
  const [twoFAEmail, setTwoFAEmail] = useState("");
  const [twoFAUserId, setTwoFAUserId] = useState("");
  const [showOrgSelector, setShowOrgSelector] = useState(false);
  const [orgMemberships, setOrgMemberships] = useState([]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email: loginForm.email, password: loginForm.password });
      if (data.requires_2fa) {
        setShow2FA(true);
        setTwoFAEmail(data.email);
        setTwoFAUserId(data.user_id);
        setLoading(false);
        return;
      }
      if (data.access_token && data.role) {
        localStorage.setItem("access_token", data.access_token);
        localStorage.setItem("user_role", data.role);
        if (data.org_type) localStorage.setItem("org_type", data.org_type);
        if (data.user_id) localStorage.setItem("user_id", data.user_id);
        toast.success("Login successful!");
        setTimeout(() => { window.location.replace("/dashboard"); }, 100);
      } else {
        toast.error("Invalid response");
        setLoading(false);
      }
    } catch (err) {
      toast.error("Login failed: " + (err.response?.data?.detail || "Invalid credentials"));
      setLoading(false);
    }
  };

  if (showOrgSelector) {
    return <OrgSelector memberships={orgMemberships} />;
  }

  if (show2FA) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center py-12 px-4">
        <TwoFactorVerify
          email={twoFAEmail}
          userId={twoFAUserId}
          onSuccess={(data) => {
            if (data.requires_membership_selection && data.memberships?.length > 1) {
              setOrgMemberships(data.memberships);
              setShowOrgSelector(true);
              return;
            }
            toast.success("Login successful!");
            setTimeout(() => { window.location.replace("/landlord/dashboard"); }, 100);
          }}
          onBack={() => { setShow2FA(false); }}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-green-50 flex items-center justify-center py-12 px-4">
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="text-center space-y-2">
          <div className="flex items-center justify-center space-x-2 mb-2"><Building2 className="h-8 w-8 text-emerald-600" /><span className="text-2xl font-bold">Estate Flow</span></div>
          <div className="flex items-center justify-center space-x-2 text-green-600"><Home className="h-6 w-6" /><span className="font-medium text-lg">Landlord Portal</span></div>
          <CardTitle>Welcome Back</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2"><Label>Email</Label><Input type="email" placeholder="landlord@example.com" value={loginForm.email} onChange={(e) => setLoginForm({...loginForm, email: e.target.value})} required /></div>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label>Password</Label>
                <button 
                  type="button"
                  onClick={() => navigate('/forgot-password')}
                  className="text-sm text-emerald-600 hover:text-emerald-700 hover:underline"
                >
                  Forgot password?
                </button>
              </div>
              <Input type="password" value={loginForm.password} onChange={(e) => setLoginForm({...loginForm, password: e.target.value})} required />
            </div>
            <Button type="submit" className="w-full bg-green-600 hover:bg-green-700" disabled={loading}>{loading ? 'Signing in...' : <><LogIn className="mr-2 h-4 w-4" />Sign In</>}</Button>
          </form>
          <div className="mt-4 text-center"><Button variant="link" onClick={() => navigate('/')}>Back to Home</Button></div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LandlordLogin;